import React from "react";
import { Flex, Text } from "@chakra-ui/react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { PathPickProductCardComparisonLayout } from "@/features/pathpick/types";
import {
  PathPickMarketRecommendation,
  PATH_PICK_MARKET_LABEL,
} from "@/features/pathpick/utils/market";

interface Props {
  recommendation: PathPickMarketRecommendation;
  layout: PathPickProductCardComparisonLayout;
}

/**
 * 카드 하단 추천 줄 — 마진이 높은 마켓과 그 차액.
 *
 * 양쪽 마진이 다 있을 때만 렌더된다(호출부가 recommendation null 이면 줄 자체를 그리지 않는다).
 * `side`는 마켓명을 왼쪽에 두고 금액을 오른쪽에 모으고, `stack`은 세 줄을 좌측 정렬한다.
 */
function PathPickProductCardRecommendation({ recommendation, layout }: Props) {
  const { t, tCurrencyString } = useI18nHelper();
  const formatKrw = (value: number) =>
    tCurrencyString(value, { type: "SYMBOL", code: "KRW" });

  const marketText = t("path_pick:market.recommended", {
    market: PATH_PICK_MARKET_LABEL[recommendation.market],
  });
  const marginText = t("path_pick:market.recommended_margin", {
    amount: formatKrw(recommendation.margin),
  });
  // 두 마진이 같으면 "더 높음"이 거짓이 되므로 차액 줄을 생략한다.
  const diffText =
    recommendation.diff > 0
      ? t("path_pick:market.recommended_diff", {
          market: PATH_PICK_MARKET_LABEL[recommendation.comparedMarket],
          amount: formatKrw(recommendation.diff),
        })
      : undefined;

  const isSide = layout === "side";

  return (
    <Flex
      flexDirection={isSide ? "row" : "column"}
      justifyContent={"space-between"}
      alignItems={isSide ? "center" : "stretch"}
      flexWrap={"wrap"}
      gap={isSide ? "0 8px" : "2px"}
    >
      <Text
        textStyle={"body-sm-bold"}
        color={"#1272EF"}
        flexShrink={0}
        // 나란히 보기에서 이 줄은 가로 한 줄에 들어가야 한다 — 넘치면 금액 열이 통째로 아래로 접힌다.
        // 가장 좁은 카드는 시작 가이드의 240px(안쪽 215px)이고, 운영도 창 폭이 열 개수 경계에
        // 걸리면 그 근처까지 좁아진다. 우측 금액 열이 이미 11px로 140px 안팎을 쓰기 때문에
        // 라벨이 12px이면 215px에 몇 px 차로 걸린다. 옆 마켓 블록의 마켓 라벨도 같은 이유로 11px다.
        fontSize={"11px"}
      >
        {marketText}
      </Text>
      <Flex
        flexDirection={"column"}
        alignItems={isSide ? "flex-end" : "flex-start"}
        marginLeft={isSide ? "auto" : undefined}
      >
        <Text textStyle={"body-sm-bold"} color={"#1272EF"}>
          {marginText}
        </Text>
        {diffText && (
          <Text
            textStyle={"caption-medium"}
            color={"#1272EF"}
            fontSize={"11px"}
          >
            {diffText}
          </Text>
        )}
      </Flex>
    </Flex>
  );
}

export default PathPickProductCardRecommendation;
